import * as yup from "yup";
import {
  type AnyObject,
  DateSchema,
  MixedSchema,
  NumberSchema,
  ObjectSchema,
  StringSchema,
} from "yup";
import { isNil, join, round, some } from "lodash";
import { hasNumberValue, isCurrency } from "common/components/Money/utils";
import { type EditMoneyType } from "common/forms/fields/types";
import { type Range } from "common/containers/Common/Assets/utils/getFields";
import { isSameDay, isBefore, isValid, isEqual } from "date-fns";

import moment from "moment";

export type Flags = "s" | "d" | "";
export type Maybe<T> = T | null | undefined;

declare module "yup" {
  interface NumberSchema {
    decimal: (places: number) => NumberSchema;
  }
  interface ObjectSchema<
    TIn extends Maybe<AnyObject>,
    TContext = AnyObject,
    // important that this is `any` so that using `ObjectSchema<MyType>`'s default
    // will match object schema regardless of defaults
    TDefault = any,
    TFlags extends Flags = "",
  > {
    atLeastOneOf: (
      fields: string[] | Record<string, unknown>[],
    ) => ObjectSchema<TIn, TContext, TDefault, TFlags>;
    valuesAreUnique: (message: string) => ObjectSchema<TIn, TContext, TDefault, TFlags>;
  }
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = "",
  > {
    oneOfWithNull(
      options: (string | undefined | null)[],
    ): StringSchema<TType, TContext, TDefault, TFlags>;
    someFieldFilledIn(
      fieldNamesToTest: string[],
      errorMessage?: string,
    ): StringSchema<TType, TContext, TDefault, TFlags>;
    oneOfWithEmpty: (fields: string[]) => StringSchema<TType, TContext, TDefault, TFlags>;
  }

  interface MixedSchema {
    oneOfWithNull: (fields: any[], key?: string) => MixedSchema;
    oneOfWithEmpty: (fields: any[], key?: string) => MixedSchema;
  }
  interface DateSchema<
    TType extends Maybe<Date>,
    TContext = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = "",
  > {
    tsAsOfDate: (
      conflictingDates: Date[],
    ) => DateSchema<NonNullable<TType>, TContext, TDefault, TFlags>;
  }
}

// Add custom schema types and methods for yup
// a "path" must be specified in createError to attach the error message
// to the errors validation object; "path" can be any string

// override default type error messages with less casting information
// when a type validationError occurs than what yup provides
yup.setLocale({
  mixed: {
    notType: ({ path, type }) => `${path} must be a ${type}`,
  },
});

const MAX_INT = 2 ** 31 - 1;
/**
 * add custom positive integer with max 32 bits
 */
export const positiveInt = yup
  .number()
  .integer()
  .positive(({ label }) => `${label} must be a positive number`)
  .max(MAX_INT);

export const zeroOrPositiveInt = yup.number().integer().min(0).max(MAX_INT);

/**
 * returns a partial yup schema for RangeToggleField
 * @param fieldLabel the display label for the input
 * @param places the decimal place precision
 */
export const range = (fieldLabel: string, places = 2) => {
  return yup.object().shape({
    minVal: yup
      .number()
      .decimal(places)
      .default(null)
      .nullable()
      .when(["maxVal", "isRange"], {
        is: (maxVal: number | null, isRange: boolean | null) =>
          isRange && maxVal !== null && isFinite(Number(maxVal)),
        then: (schema) =>
          schema.lessThan(
            yup.ref("maxVal"),
            ({ label }) => `${label} must be less than the maximum value`,
          ),
      })
      .label(`${fieldLabel} minimum value`),
    maxVal: yup
      .number()
      .decimal(places)
      .when("isRange", ([isRange]: boolean[], maxValSchema) =>
        isRange
          ? maxValSchema.required(({ label }) => `${label} is required`)
          : maxValSchema.default(null).nullable(),
      )
      .label(`${fieldLabel} maximum value`),
    isRange: yup.boolean().default(null).nullable(),
  });
};

/**
 * a transform function to convert an empty string to null
 * useful for working with numerical inputs that are nullable
 * number().transform(emptyStringToNull).nullable()
 */
export function emptyStringToNull(value: any, originalValue: string) {
  if (typeof originalValue === "string" && originalValue.trim() === "") {
    return null;
  }
  return value;
}

/**
 * extends yup.object with atLeastOneOf method
 * checks that at least one of the fields in the list is not undefined, null, or the empty string
 * @param {string[]} fields
 * @return {true | yup.ValidationError}
 */
yup.addMethod(ObjectSchema, "atLeastOneOf", function atLeastOneOf(fields: string[]) {
  const message = `One of these keys cannot be undefined, null, or empty string: ${join(
    fields,
    ", ",
  )}`;
  return this.test("atLeastOneOf", message, function testHasAtLeastOneOf(value) {
    if (!value) {
      return false;
    }
    const valid = fields.some((field) => !(isNil(value[field]) || value[field] === ""));
    return valid || this.createError({ path: this.path, message });
  });
});

/**
 * extends yup.string with oneOfWithNull method
 * Must check if string is null for the "one of" functionality
 * known issue with yup https://github.com/jquense/yup/issues/104
 * @param {string[]} options
 * @return {true | yup.ValidationError}
 */
yup.addMethod(
  StringSchema,
  "oneOfWithNull",
  function oneOfWithNull(options: (string | undefined | null)[]) {
    const message = `Invalid option selected, must be one of: ${join(options.concat(), ", ")}`;
    return this.test("oneOfWithNull", message, function testOneOfWithNull(value) {
      if (value === null) {
        return true;
      }
      const valid = options.includes(value);
      return valid || this.createError({ path: this.path, message });
    });
  },
);

type PotentialSchemaValue =
  | string
  | number
  | boolean
  | Date
  | EditMoneyType
  | EditMoneyType[]
  | Range
  | null;

/**
 * Tests for some value's existence given a list of names to test against on the current schema.
 * Note: This function does not take into account nested objects or non-currency arrays. It will
 * assume there is a value based on a value's truthyness
 * @param {string[]} options
 * @return {true | yup.ValidationError}
 */
yup.addMethod(
  StringSchema,
  "someFieldFilledIn",
  function someFieldFilledIn(
    fieldNamesToTest: string[],
    errorMessage = "At least one field must be filled out.",
  ) {
    return this.test("someValueFilledIn", errorMessage, function testSomeFieldFilledIn() {
      return fieldNamesToTest.some((fieldName) => {
        const value: PotentialSchemaValue = this.resolve(yup.ref(fieldName));
        if (Array.isArray(value) && value.every(isCurrency)) {
          // is multi-currency
          return value.some(hasNumberValue);
        } else if (isCurrency(value)) {
          return hasNumberValue(value);
        } else if (typeof value === "number") {
          return true;
        }
        return Boolean(value);
      });
    });
  },
);

/**
 * extends StringSchema with oneOfWithEmpty method allows
 * a value to be "", undefined, or null, or a matching string in the options
 * array.
 * Useful for select field when the placeholder --Select-- has value "" or the
 * select field is pristine and the value is undefined
 * @param {string[]} options
 * @return {true | yup.ValidationError}
 */
yup.addMethod(StringSchema, "oneOfWithEmpty", function oneOfWithEmpty(options = []) {
  const message = `Invalid option selected, must be one of: ${join(options.concat(""), ", ")}`;
  return this.test("oneOfWithEmpty", message, function testOneOfWithEmpty(value) {
    if (value === "" || value === null || value === undefined || options.includes(value)) {
      return true;
    }
    return this.createError({ path: this.path, message });
  });
});

/**
 * extends yup.mixed with oneOfWithNull method
 * Must check if mixed is null for the "one of" functionality
 * known issue with yup https://github.com/jquense/yup/issues/104
 * @param {string[]} options
 * @return {true | yup.ValidationError}
 */
yup.addMethod(MixedSchema, "oneOfWithNull", function oneOfWithNull(options, key) {
  const message = `Invalid option selected, must be one of: ${join(
    options.concat(null),
    ", ",
  )}`;
  return this.test("oneOfWithNull", message, function testOneOfWithNull(value) {
    if (value === null) {
      return true;
    }
    let valid = false;
    if (typeof value === "string" && !key) {
      valid = options.includes(value);
    }
    if (key) {
      valid = some(options, [key, value]);
    }
    return valid || this.createError({ path: this.path, message });
  });
});

/**
 * extends yup.mixed with oneOfWithEmpty method allows
 * a value to be "", undefined, or null, a matching string in the options
 * array, or a matching value in the options array of objects with provided key
 * Useful for select field when the placeholder --Select-- has value "" or the
 * select field is pristine and the value is undefined
 * @param {string[]} options
 * @return {true | yup.ValidationError}
 */
yup.addMethod(MixedSchema, "oneOfWithEmpty", function oneOfWithEmpty(options = [], key) {
  const message = `Invalid option selected, must be one of: ${join(options.concat(""), ", ")}`;
  return this.test("oneOfWithEmpty", message, function testOneOfWithEmpty(value) {
    if (value === "" || value === null || value === undefined) {
      return true;
    }
    let valid = false;
    if (typeof value === "string" && !key) {
      valid = options.includes(value);
    }
    if (key) {
      valid = some(options, [key, value]);
    }
    return valid || this.createError({ path: this.path, message });
  });
});

/**
 * extends yup.number with "decimal" method
 * parses a number as a float and fixes to n places
 * @param {number} places
 * @return {string} formatted decimal
 */
yup.addMethod(NumberSchema, "decimal", function decimalTransform(places: number) {
  // value = when casted to number
  // originalValue = before cast to number
  return this.transform((value, originalValue) => {
    if (isNil(originalValue) || originalValue === "") {
      return null;
    }
    return round(value, places);
  });
});

/**
 * Generates a nullable number
 * @param label - Label to display for error messages
 */
export const nullableNumber = (label: string) => {
  return yup
    .number()
    .label(label)
    .transform(emptyStringToNull)
    .typeError(`${label} must be a number`)
    .nullable();
};

/**
 * Helper function to generate positive decimal schemas. Allows optional max param.
 * @param label - Label to display for error messages
 * @param places - fixes the number to n places
 * @param max - Optional max value allowed for number
 */
export const positiveDecimal = (label: string, places: number, max?: number) => {
  const postiveDecimal = yup
    .number()
    .decimal(places)
    .label(label)
    .transform(emptyStringToNull)
    .typeError(`${label} must be a positive number`)
    .positive(`${label} must be a positive number`)
    .nullable();

  return max !== undefined ? postiveDecimal.max(max) : postiveDecimal;
};

/**
 * method to validate time series as-of-dates. The as-of date is required and can't match
 * another existing as of date. Pass in an array of the other as-of-dates (all existing dates for a new
 * time series entry, other dates in case of editing an existing entry) and this will validate that
 * the date chosen does not overlap.
 * @param {Date[]} conflictingDates - The other time series dates that exist for the set of fields being
 * edited.
 */
yup.addMethod(DateSchema, "tsAsOfDate", function tsAsOfDate(conflictingDates: Date[]) {
  return this.required()
    .test(
      "not-same-date",
      "There is already an entry for this as-of date. To make changes or additions to that as-of date's data, edit that entry",
      function notSameDate(value: Date) {
        return !conflictingDates.some(
          (conflictingDate) => value && isSameDay(conflictingDate, value),
        );
      },
    )
    .label("As of date");
});

/**
 * Use moment to strip timezome information, this makes the client work in UTC time too regardless of client locale
 * @param date Date object to drop timezone info for and to take date as-is
 */
export function dropDateTimezone(date: Date) {
  return moment(date.toUTCString()).utcOffset(-8, true).toDate();
}
// Some schemas have `date()` fields which are nullable. Added this check to return null when it is null to ensure
// the schemas validating the form correctly.
export function dropDateTimezoneSchema(date: Date | null) {
  if (date == null) {
    return null;
  }
  return dropDateTimezone(date);
}

/**
 * Check that date1 is before or equal to date2.
 *
 * @param date1 Date to validate. This date must be before date2.
 * @param date2 Date to check against date1.
 */
export function validateDates(
  date1: Date | null | undefined,
  date2: Date | null | undefined,
): boolean {
  if (date1 && date2 && isValid(date1) && isValid(date2) && !isBefore(date1, date2)) {
    return isEqual(date1, date2);
  }
  return true;
}
